import React, { useState } from 'react';
import { AppUser } from './types';
import { mockedFM } from './mock-data';
import { AuthPermission, AuthScope, PermissionScope } from '@bechtel/types';
import { AppUserProps } from './types';
import { t } from '@lingui/macro';

type MaybeUser = AppUser | null;

// rightsmockedFM
function hasRight(rightName: string, user: MaybeUser) {
  if (!rightName || !user) return false;
  const { rights } = user;
  if (!rights || rights.length === 0) return false;
  return rights.some((r) => r.rightName === rightName);
}
export function hasPermissionAndScope(permission: AuthPermission, scope: AuthScope, user: MaybeUser) {
  if (!permission || !scope || !user) return false;
  const { permissions } = user;
  if (!permissions || permissions.length === 0) return false;
  return permissions.some((ps) => ps.permission.permissionId === permission && ps.scope.id === scope);
}

// permissions by permissionScope
export function hasPermissionScope(permissionScope: PermissionScope, user: MaybeUser) {
  if (!permissionScope || !user) return false;
  return hasPermissionAndScope(permissionScope.permission, permissionScope.scope, user);
}

// all permissions
export function hasAllPermissionScopes(permScopes: PermissionScope[], user: MaybeUser) {
  if (!permScopes || !user || permScopes.length <= 0) return false;
  const { permissions } = user;
  if (!permissions || permissions.length === 0) return false;
  return permScopes.every((inputPermission) => {
    return permissions.some(
      (ps) => ps.permission.permissionId === inputPermission.permission && ps.scope.id === inputPermission.scope
    );
  });
}

// patterned after https://kentcdodds.com/blog/how-to-optimize-your-context-value

const StateContext: React.Context<MaybeUser> = React.createContext<MaybeUser>(null); //this.store);
const UpdaterContext: React.Context<React.Dispatch<React.SetStateAction<MaybeUser>>> =
  React.createContext<
    React.Dispatch<React.SetStateAction<MaybeUser>>
    //react create context accepts null, bug with type definations
    //@ts-ignore react create context accepts null, bug with type definations
  >(null); //this.store);

function useAppUser(): AppUserProps {
  const user = React.useContext(StateContext);
  if (typeof user === undefined) {
    throw new Error(t`useAppUser must be used within a AppUserProvider`);
  }
  // added as per Vel's suggestion -> hook should return rights checker function!
  const userHasRight = React.useCallback((rightName: string) => hasRight(rightName, user), [user]);
  const userHasPermissionAndScope = React.useCallback(
    (permission: AuthPermission, scope: AuthScope) => hasPermissionAndScope(permission, scope, user),
    [user]
  );
  const userHasPermissionScope = React.useCallback(
    (permissionScope: PermissionScope) => hasPermissionScope(permissionScope, user),
    [user]
  );
  const userHasAllPermissionScopes = React.useCallback(
    (permissionScopes: PermissionScope[]) => hasAllPermissionScopes(permissionScopes, user),
    [user]
  );

  //
  return {
    user,
    hasRight: userHasRight,
    hasPermissionAndScope: userHasPermissionAndScope,
    hasPermissionScope: userHasPermissionScope,
    hasAllPermissionScopes: userHasAllPermissionScopes,
  };
}

function useAppUserUpdater() {
  const setAppUser = React.useContext(UpdaterContext);
  if (typeof setAppUser === undefined) {
    throw new Error(t`useAppUserUpdater must be used within a AppUserProvider`);
  }
  const userAuthorized = React.useCallback((authorizedUser: AppUser) => setAppUser(authorizedUser), [setAppUser]);
  const userNotAuthorized = React.useCallback(() => setAppUser(null), [setAppUser]);
  return { userAuthorized, userNotAuthorized };
}

function AppUserProvider(props: any) {
  // TODO: REMOVE mockedFM once backend has login functionality
  // TODO: Remove Rights functionality once RBAC is implemented in all modules
  const [appUser, setAppUser] = useState<MaybeUser>(props.user || mockedFM);

  return (
    <StateContext.Provider value={appUser}>
      <UpdaterContext.Provider value={setAppUser}>{props.children}</UpdaterContext.Provider>
    </StateContext.Provider>
  );
}

export { AppUserProvider, useAppUser, useAppUserUpdater };
